import React, { useState, useEffect } from 'react';
import { getFirestore, collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, listAll, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { FaFolder, FaFileAudio, FaArrowLeft, FaPlus, FaEllipsisV, FaCloudUploadAlt, FaImage } from 'react-icons/fa';

import ExplorerGrid from './ExplorerGrid'; 
import ContextMenu from './ContextMenu';
import FileDropper from './FileDropper';
import ProgramModal from './ProgramModal';

const RadioManagerTab = () => {
  const [view, setView] = useState('root'); // 'root' or 'folder'
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [menuPos, setMenuPos] = useState(null);

  const db = getFirestore();
  const storage = getStorage();

  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setMenuPos({ x: e.pageX, y: e.pageY, item });
  };

  const handleDeleteFolder = async (id) => {
    // 1. Delete Firestore Doc
    await deleteDoc(doc(db, 'radio_folders', id));
    // 2. (Cloud function will handle storage cleanup based on delete trigger)
    setView('root');
  };

  // 1. Fetch Folders (Programs) from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'radio_folders'), (snap) => {
      const folderList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort Alphanumerically by programName
      setFolders(folderList.sort((a, b) => a.programName.localeCompare(b.programName)));
    });
    return unsub;
  }, [db]);

  // 2. Fetch Files when entering a folder
  const enterFolder = async (folder) => {
    if (!folder) return;
    
    setCurrentFolder(folder);
    setView('folder'); // This switches the Grid view
    
    try {
      // This points to the storage path we defined earlier
      const storageRef = ref(storage, `radio/programs/${folder.id}`);
      const res = await listAll(storageRef);
      
      const fileData = res.items.map(item => ({
        name: item.name,
        path: item.fullPath
      }));
      
      // Sort alphanumerically to match your ownCloud view (AC 1, AC 2...)
      setFiles(fileData.sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      ));
    } catch (err) {
      console.error("Error loading folder files:", err);
      setFiles([]); // Reset if folder is empty or path doesn't exist yet
    }
  };
  const handleUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    setIsUploading(true);

    for (const file of newFiles) {
      const fileRef = ref(storage, `radio/programs/${currentFolder.id}/${file.name}`);
      await uploadBytesResumable(fileRef, file);
    }
    
    setIsUploading(false);
    enterFolder(currentFolder); // Refresh view
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-slate-50 min-h-screen rounded-3xl shadow-inner" onContextMenu={(e) => e.preventDefault()}>
      
      {/* Explorer Toolbar */}
      <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          {view === 'folder' && (
            <button onClick={() => setView('root')} className="p-2 hover:bg-slate-100 rounded-full">
              <FaArrowLeft className="text-slate-600" />
            </button>
          )}
          <h2 className="text-xl font-black text-slate-800">
            {view === 'root' ? 'Radio Storage' : currentFolder.programName}
          </h2>
        </div>
        
        <div className="flex gap-2">
          {view === 'root' ? (
            /* ✅ FIXED: Added onClick handler to open ProgramModal */
            <button 
              onClick={() => { setEditingFolder(null); setIsModalOpen(true); }}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-md hover:bg-purple-700 transition-all"
            >
              <FaPlus /> New Program
            </button>
          ) : (
            <label className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-md cursor-pointer hover:bg-blue-700 transition-all">
              <FaCloudUploadAlt /> Upload Files
              <input type="file" multiple className="hidden" onChange={handleUpload} />
            </label>
          )}
        </div>
      </div>

      {isUploading && (
        <div className="fixed bottom-10 right-10 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-bounce">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span className="text-xs font-black uppercase tracking-widest">Syncing with Storage...</span>
        </div>
      )}
      
      {/* 2. Drag & Drop Wrapper */}
      <FileDropper 
        onUpload={(files) => handleUpload({ target: { files } })} 
        isUploading={isUploading}
      >
        {/* 3. The Explorer Grid (Line 155 Fixed) */}
        <ExplorerGrid 
          view={view}
          folders={folders}
          files={files}
          onFolderClick={(folder) => { setCurrentFolder(folder); setView('folder'); }}
          onEdit={(folder) => { setEditingFolder(folder); setIsModalOpen(true); }} // (Line 160 Fixed)
          onContextMenu={handleContextMenu}
          isUploading={isUploading}
        />
      </FileDropper>

      {/* 4. Context Menu (Line 172 Fixed) */}
      {menuPos && (
        <ContextMenu 
          x={menuPos.x} 
          y={menuPos.y} 
          item={menuPos.item}
          onClose={() => setMenuPos(null)}
          onEdit={(item) => { setEditingFolder(item); setIsModalOpen(true); }}
          onDelete={handleDeleteFolder}
          onOpen={(folder) => { setCurrentFolder(folder); setView('folder'); }}
        />
      )}

      {/* 5. The Modal */}
      <ProgramModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingFolder(null); }} 
        editingFolder={editingFolder} 
      />
    </div>
  );
};

export default RadioManagerTab;